import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Link,
  Chip,
  Divider,
  Button,
  MenuItem,
  Tooltip,
  SelectChangeEvent,
} from "@mui/material";
import Select from "../common/Select";
import {
  ChevronDown,
  ChevronUp,
  X,
  MemoryStick,
  ListTodo,
  EllipsisVertical,
  PersonStanding,
  Info,
} from "lucide-react";
import useIsMobile from "../../components/hooks/useIsMobile";
import DateDisplay from "../../components/common/DateDisplay";
import { AdvisorApplication } from "../../protogen/advisor_pipeline_service_pb";
import MetadataSection from "../../components/common/MetadataSection";
import {
  useUpdateAdvisorApplication,
  useSendAdvisorMeetingInvitation,
  useSendAdvisorOfferEmail,
  useSendAdvisorBackgroundCheckEmail,
  useSendAdvisorOnboardingEmail,
} from "../../services/advisorPipeline";
import { formatUSPhoneNumber } from "../../common/utils";
import {
  BasicDetail,
  ContactDetail,
  MultiSelectDetail,
  TrackingDetails,
} from "./Details";
import {
  AdvisorApplicationStatus,
  NEXT_PIPELINE_STATUS_MAP,
  SCREENERS,
} from "./advisorFlow";
import PopperMenu, { createMenuOption } from "components/common/PopperMenu";
import AdvisorApplicationNotes from "./AdvisorApplicationNotes";
import LinkRouter from "components/navigation/LinkRouter";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import WorkProfile from "./WorkProfile";
import { EntityMetadata } from "protogen/common_pb";
import { useCreateAdvisorNux } from "services/nux";

const leftFlex = 3;
const rightFlex = 5;
// Mapping of status name to Title, potential subtext, and color
const STATUS_MAP: Record<
  AdvisorApplicationStatus,
  {
    label: string;
    bgColor: string;
    textColor: string;
    subtext?: string;
    subtextColor?: string;
    subtextBgColor?: string;
  }
> = {
  // New
  in_review: {
    label: "New application",
    bgColor: "#FFF3D6", // Yellow tint
    textColor: "#6B6E7B",
  },
  // In process
  qualified_app: {
    label: "In process",
    subtext: "Qualified App",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  waitlist: {
    label: "In process",
    subtext: "Qualified - Waitlist",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  interview: {
    label: "In process",
    subtext: "Interview invitation sent",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  no_show: {
    label: "In process",
    subtext: "No Show Interview",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  meeting: {
    label: "In process",
    subtext: "Meeting",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  ready_ttt: {
    label: "In process",
    subtext: "Ready for TTT",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  ttt_sent: {
    label: "In process",
    subtext: "TTT Sent",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  send_offer: {
    label: "In process",
    subtext: "Send Offer",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  offer_sent: {
    label: "In process",
    subtext: "FA Offer Sent",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  accepted: {
    label: "In process",
    subtext: "Offer Accepted",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  send_background_check: {
    label: "In process",
    subtext: "Send Background Check",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  background_check_complete: {
    label: "In process",
    subtext: "Background Check Complete",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  schedule_onboarding: {
    label: "In process",
    subtext: "Invite + Schedule Onboarding",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  onboarding: {
    label: "In process",
    subtext: "Onboarding Scheduled",
    bgColor: "#FFF3D6",
    textColor: "#6B6E7B",
  },
  active: {
    label: "Active",
    subtext: "Currently working",
    bgColor: "#E6F4EA", // Green tint
    textColor: "#1E8E3E",
  },
  // All
  on_hold: {
    label: "On Hold",
    subtext: "Temporarily paused",
    bgColor: "#FFE0E0", // Red tint
    textColor: "#D93025",
  },
  rejected: {
    label: "Rejected",
    subtext: "Not moving forward",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
  churned: {
    label: "Churned",
    subtext: "No longer active",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
  never_active: {
    label: "Churned - Never Active",
    subtext: "Did not start",
    bgColor: "#FFE0E0",
    textColor: "#D93025",
  },
};

const StatusAction = ({
  label,
  variant,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  variant?: "primary" | "warning" | "error";
  onClick: () => void;
}) => {
  // Define styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case "primary":
        return {
          variant: "contained" as const,
          sx: {
            backgroundColor: "#2A5A8A", // Dark blue background
            color: "#FFFFFF", // White text
            border: "1px solid #2A5A8A",
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#1E4A7A", // Darker blue on hover
              border: "1px solid #1E4A7A",
            },
            "&:disabled": {
              backgroundColor: "#B0B0B0",
              color: "#FFFFFF",
              border: "1px solid #B0B0B0",
            },
          },
        };
      case "error":
        return {
          variant: "outlined" as const,
          sx: {
            backgroundColor: "transparent",
            color: "#D93025", // Red text
            border: "1px solid #D93025", // Red border
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#FFF5F5", // Light red background on hover
              border: "1px solid #D93025",
            },
            "&:disabled": {
              color: "#B0B0B0",
              border: "1px solid #B0B0B0",
            },
          },
        };
      default:
        return {
          variant: "outlined" as const,
          sx: {
            backgroundColor: "transparent",
            color: "#5B9BD5", // Blue text
            border: "1px solid #5B9BD5", // Blue border
            borderRadius: "20px",
            textTransform: "none" as const,
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "#F0F8FF", // Light blue background on hover
              border: "1px solid #5B9BD5",
            },
            "&:disabled": {
              color: "#B0B0B0",
              border: "1px solid #B0B0B0",
            },
          },
        };
    }
  };

  const buttonStyles = getButtonStyles();

  return (
    <Button
      variant={buttonStyles.variant}
      size="small"
      onClick={onClick}
      disabled={disabled}
      sx={buttonStyles.sx}
    >
      {label}
    </Button>
  );
};

const StatusActions = ({
  currentStatus,
  disabled,
  onClick,
}: {
  currentStatus: AdvisorApplicationStatus;
  disabled: boolean;
  onClick: (status: AdvisorApplicationStatus) => Promise<void>;
}) => {
  const nextStatuses = NEXT_PIPELINE_STATUS_MAP[currentStatus];
  return (
    <>
      {nextStatuses.map((status) => (
        <StatusAction
          key={status.name}
          label={
            STATUS_MAP[status.name].subtext || STATUS_MAP[status.name].label
          }
          variant={status.variant}
          disabled={disabled}
          onClick={() => onClick(status.name)}
        />
      ))}
    </>
  );
};

const StatusChip = ({
  label,
  bgColor,
  textColor,
  subtext,
  subtextColor,
  subtextBgColor,
}: {
  label: string;
  bgColor: string;
  textColor: string;
  subtext?: string;
  subtextColor?: string;
  subtextBgColor?: string;
}) => {
  const isMobile = useIsMobile();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "start",
        gap: "2px",
      }}
    >
      <Chip
        label={label}
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          fontWeight: 600,
          fontSize: "12px",
          height: "24px",
        }}
      />
      {subtext && (
        <Chip
          label={subtext}
          sx={{
            backgroundColor: subtextBgColor || "#F5F5F5",
            color: subtextColor || "#6B6E7B",
            fontWeight: 400,
            fontSize: "11px",
            height: "20px",
          }}
        />
      )}
    </Box>
  );
};

const getStatus = (application: AdvisorApplication) => {
  const status = application.status.toLowerCase();
  const statusConfig = STATUS_MAP[status as keyof typeof STATUS_MAP] || {
    label: status,
    bgColor: "#FFF",
    textColor: "#6B6E7B",
  };

  return <StatusChip {...statusConfig} />;
};

const ApplicationDetails = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const isMobile = useIsMobile();
  return (
    <Box sx={{ padding: "0", flex: leftFlex }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="10px"
      >
        {isMobile && (
          <Typography
            variant="body"
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <DateDisplay
              date={new Date(Number(application.lastActivitySec) * 1000)}
            />
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Contact info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography variant="bodyHeavy">Contact info</Typography>
            <ContactDetail
              icon="Phone"
              content={
                <Link
                  href={`/inbox/phone/create?phone=${encodeURIComponent(
                    application.phoneNumber || "",
                  )}`}
                  sx={{ fontWeight: 500 }}
                >
                  {formatUSPhoneNumber(application.phoneNumber)}
                </Link>
              }
            />
            <ContactDetail
              icon="Mail"
              content={
                <Link
                  href={`/inbox/email/create?email=${encodeURIComponent(
                    application.email || "",
                  )}`}
                  sx={{ fontWeight: 500 }}
                >
                  {application.email}
                </Link>
              }
            />
            <ContactDetail
              icon="MapPin"
              content={
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    application.location,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 500 }}
                >
                  {application.location}
                </Link>
              }
            />
            <ContactDetail
              icon="Linkedin"
              content={
                <Link
                  href={application.linkedinProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 500 }}
                >
                  LinkedIn
                </Link>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ApplicationMetadata = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const { request } = useUpdateAdvisorApplication();
  const metadata = application.metadata || new EntityMetadata({});
  return (
    <MetadataSection
      metadata={metadata}
      saveMetadata={async (metadata) => {
        await request({
          ref: application.ref,
          updatedStatus: "",
          shouldUpdateMetadata: true,
          updatedMetadata: metadata,
        });
      }}
      entityType={"advisor"}
    />
  );
};

const ApplicationContent = ({
  application,
}: {
  application: AdvisorApplication;
}) => {
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "24px",
        borderTop: "1px solid #E2E3E4",
        padding: isMobile ? "20px 0 0" : "20px 0",
      }}
    >
      {/* Left */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: leftFlex,
          gap: "20px",
        }}
      >
        <BasicDetail
          label="Cities with expertise"
          content={application.citiesWithExpertise}
        />
        <MultiSelectDetail
          label={"Superpowers"}
          Icon={MemoryStick}
          items={application.superpowers}
        />
        <BasicDetail
          label="Organization strategy"
          content={application.organizationStrategy}
        />
        <BasicDetail
          label="Has flexibility"
          content={application.hasFlexibility ? "Yes" : "No"}
        />
        <BasicDetail
          label="Willing to run errands"
          content={application.willingToRunErrands ? "Yes" : "No"}
        />
        <MultiSelectDetail
          label="Interested in offering these additional services"
          Icon={PersonStanding}
          items={application.errandSelections}
        />
        <MultiSelectDetail
          label="Favorite tasks"
          Icon={ListTodo}
          items={application.favoriteTasks}
        />
        <BasicDetail
          label="Growth strategy"
          content={application.businessPlan}
        />
        <BasicDetail label="Advisor Interest" content={application.whyFa} />
        <BasicDetail
          label="Sample plan"
          content={application.friendVisitPlan}
        />
        <BasicDetail
          label="Task prioritization"
          content={application.taskPrioritization}
        />
        <BasicDetail label="Referral" content={application.referralSource} />
        <TrackingDetails trackingParameters={application.trackingParams} />
      </Box>
    </Box>
  );
};

interface AdvisorApplicationCardProps {
  initialApplication: AdvisorApplication;
  forceOpen?: boolean;
  onClose: () => void;
  onOpen: () => void;
  hideActions?: boolean;
}

export default ({
  initialApplication,
  forceOpen = false,
  onClose,
  onOpen,
  hideActions = false,
}: AdvisorApplicationCardProps) => {
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();
  const [open, setOpen] = useState(forceOpen);
  const [application, setApplication] = useState(initialApplication);
  const { request: updateRequest, loading } = useUpdateAdvisorApplication();
  const { request: createAdvisorNuxRequest, loading: createAdvisorNuxLoading } =
    useCreateAdvisorNux();
  const { request: sendInvitationRequest, loading: invitationLoading } =
    useSendAdvisorMeetingInvitation();
  const { request: sendOfferEmailRequest, loading: offerEmailLoading } =
    useSendAdvisorOfferEmail();
  const {
    request: sendBackgroundCheckEmailRequest,
    loading: backgroundCheckEmailLoading,
  } = useSendAdvisorBackgroundCheckEmail();
  const {
    request: sendOnboardingEmailRequest,
    loading: onboardingEmailLoading,
  } = useSendAdvisorOnboardingEmail();
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [selectedScreenerRef, setSelectedScreenerRef] = useState<string>("");
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    setApplication(initialApplication);
  }, [initialApplication]);

  const handleStatusMenuToggle = () => {
    setStatusMenuOpen((prevOpen) => !prevOpen);
  };
  const statusMenuAnchorRef = useRef<HTMLButtonElement>(null);

  const handleCreateAdvisorNux = async () => {
    if (createAdvisorNuxLoading) return;
    await createAdvisorNuxRequest({
      email: application.email,
    });
    pushSnack("Advisor sent invitation to create account!");
  };

  const handleSendMeetingInvitation = async () => {
    if (invitationLoading) return;
    await sendInvitationRequest({
      applicationRef: application.ref,
      screenerRef: selectedScreenerRef,
    });
    pushSnack("Applicant has been emailed to schedule a meeting!");
    setSelectedScreenerRef("");
  };

  const handleSendOfferEmail = async () => {
    if (offerEmailLoading) return;
    await sendOfferEmailRequest({
      applicationRef: application.ref,
    });
    pushSnack("Offer email sent!");
  };

  const handleSendBackgroundCheckEmail = async () => {
    if (backgroundCheckEmailLoading) return;
    await sendBackgroundCheckEmailRequest({
      applicationRef: application.ref,
    });
    pushSnack("Background check email sent!");
  };

  const handleSendOnboardingEmail = async () => {
    if (onboardingEmailLoading) return;
    await sendOnboardingEmailRequest({
      applicationRef: application.ref,
    });
    pushSnack("Onboarding email sent!");
  };

  const handleUpdate = async (
    status: string,
    triggerActions: boolean = false,
  ) => {
    if (loading) return;

    if (triggerActions) {
      if (status === "interview") {
        await handleSendMeetingInvitation();
      } else if (status === "send_offer") {
        await handleSendOfferEmail();
      } else if (status === "send_background_check") {
        await handleSendBackgroundCheckEmail();
      } else if (status === "schedule_onboarding") {
        await handleSendOnboardingEmail();
        await handleCreateAdvisorNux();
      }
    }

    const response = await updateRequest({
      ref: application.ref,
      updatedStatus: status,
      shouldUpdateMetadata: false,
      updatedMetadata: undefined,
    });
    if (response?.application) {
      setApplication(response.application);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: open ? "#FBFBFB" : "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: "12px",
          cursor: !open ? "pointer" : undefined,
          padding: isMobile ? "12px 20px" : "16px 24px",
        }}
        onClick={() => !open && handleOpen()}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: isMobile ? "space-between" : "",
          }}
        >
          <Box
            sx={{
              flex: isMobile ? "" : leftFlex,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              overflow: "hidden",
            }}
          >
            {application.advisorRef ? (
              <LinkRouter
                to={`/tools/advisor-administration/${encodeURIComponent(
                  application.advisorRef,
                )}`}
              >
                <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                  {`${application.firstName} ${application.lastName}`}
                </Typography>
              </LinkRouter>
            ) : (
              <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                {`${application.firstName} ${application.lastName}`}
              </Typography>
            )}
            <Typography variant="body" color="text.secondary">
              {application.location}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: isMobile ? "" : rightFlex,
              justifyContent: "space-between",
            }}
          >
            {!isMobile && getStatus(application)}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {!isMobile && (
                <Typography variant="body">
                  <DateDisplay
                    date={new Date(Number(application.lastActivitySec) * 1000)}
                  />
                </Typography>
              )}
              {forceOpen ? (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => handleClose()}
                >
                  <X />
                </IconButton>
              ) : (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <ChevronUp /> : <ChevronDown />}
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {isMobile && getStatus(application)}
      </Box>
      <Collapse in={open || forceOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "#ECECEC" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
          }}
        >
          {/* Top half*/}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "24px",
              padding: "20px 0",
            }}
          >
            {/* Left side */}
            <ApplicationDetails application={application} />

            {/* Right side */}
            <Box
              sx={{
                flex: rightFlex,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <ApplicationMetadata application={application} />
                {application.workProfile && (
                  <WorkProfile
                    workProfileJson={application.workProfile}
                    linkedinUrl={application.linkedinProfile}
                  />
                )}
              </Box>

              {/* Actions */}
              {!hideActions && (
                <Box
                  display="flex"
                  flexDirection={isMobile ? "row-reverse" : "row"}
                  gap={isMobile ? "24px" : "10px"}
                  justifyContent={isMobile ? "start" : "end"}
                  alignItems="center"
                >
                  <StatusActions
                    currentStatus={
                      application.status as AdvisorApplicationStatus
                    }
                    disabled={loading}
                    onClick={async (status) => {
                      await handleUpdate(status, true);
                    }}
                  />
                  <Box>
                    <IconButton
                      ref={statusMenuAnchorRef}
                      onClick={handleStatusMenuToggle}
                    >
                      <EllipsisVertical size={20} color="#8E9598" />
                    </IconButton>
                    <PopperMenu
                      open={statusMenuOpen}
                      anchorRef={statusMenuAnchorRef}
                      handleClose={() => {
                        if (selectOpen) return;
                        setStatusMenuOpen(false);
                      }}
                    >
                      <>
                        {/* Section 1: Change Status (no actions triggered) */}
                        <MenuItem
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            padding: "4px 16px",
                            cursor: "default",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Typography
                              variant="bodyHeavy"
                              color="#6B6E7B"
                              fontSize="11px"
                            >
                              CHANGE STATUS
                            </Typography>
                            <Tooltip
                              title="Manually change status. This will not trigger any actions. Please use the status buttons or manually trigger the actions."
                              placement="right"
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                <Info size={14} color="#6B6E7B" />
                              </Box>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                        {Object.entries(STATUS_MAP)
                          .filter(([key]) => key !== application.status)
                          .map(([key, config]) =>
                            createMenuOption(
                              "subtext" in config && config.subtext
                                ? config.subtext
                                : config.label,
                              async () => {
                                await handleUpdate(key, false);
                                setStatusMenuOpen(false);
                              },
                              "",
                            ),
                          )}

                        <Divider sx={{ margin: "8px 0" }} />

                        {/* Section 2: Manual Actions */}
                        <MenuItem
                          disabled
                          sx={{
                            opacity: "1 !important",
                            padding: "4px 16px",
                            cursor: "default !important",
                          }}
                        >
                          <Typography
                            variant="bodyHeavy"
                            color="#6B6E7B"
                            fontSize="11px"
                          >
                            MANUAL ACTIONS
                          </Typography>
                        </MenuItem>
                        <Box sx={{ padding: "8px 16px" }}>
                          <Select
                            variant="detachedOutlined"
                            value={selectedScreenerRef}
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              setSelectedScreenerRef(e.target.value as string);
                            }}
                            open={selectOpen}
                            onOpen={() => setSelectOpen(true)}
                            onClose={() => setSelectOpen(false)}
                            displayEmpty
                            disabled={invitationLoading}
                            sx={{ minWidth: 200 }}
                          >
                            <MenuItem value="">
                              <Typography variant="body" color="text.secondary">
                                Select screener...
                              </Typography>
                            </MenuItem>
                            {SCREENERS.map((screener) => (
                              <MenuItem
                                key={screener.user_ref}
                                value={screener.user_ref}
                              >
                                {screener.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        {createMenuOption(
                          "Send Meeting Invitation",
                          async () => {
                            await handleSendMeetingInvitation();
                            setStatusMenuOpen(false);
                          },
                          "",
                        )}
                        {createMenuOption(
                          "Send Offer Email",
                          async () => {
                            await handleSendOfferEmail();
                            setStatusMenuOpen(false);
                          },
                          "",
                        )}
                        {createMenuOption(
                          "Send Background Check Email",
                          async () => {
                            await handleSendBackgroundCheckEmail();
                            setStatusMenuOpen(false);
                          },
                          "",
                        )}
                        {createMenuOption(
                          "Send Onboarding Email",
                          async () => {
                            await handleSendOnboardingEmail();
                            setStatusMenuOpen(false);
                          },
                          "",
                        )}
                        {createMenuOption(
                          "Create Advisor Account",
                          async () => {
                            await handleCreateAdvisorNux();
                            setStatusMenuOpen(false);
                          },
                          "",
                        )}
                      </>
                    </PopperMenu>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Bottom half */}
          <ApplicationContent application={application} />
          {open && <AdvisorApplicationNotes application={application} />}
        </Box>
      </Collapse>
    </Box>
  );
};
